import { Inject, Injectable } from '@angular/core';
import { AppTheme, FONT_FAMILY_OPTION, MapAppThemeVariable, ReadRemoteThemeRS } from './theme-interface';
import { HttpClient } from '@angular/common/http';
import DefaultAppTheme from './default-theme';
import { Observable, Subject } from 'rxjs';
import { XnStorageService } from '../xn-storage';
import { IAppConfig } from '../config/app-config.model';
import { APP_CONFIG } from '../config/app-config.service';
import { LogService } from '../log.service';

const ReplaceKeySchema = {
  headerTextColor: 'textDefaultColor',
  footerBackgroundColor: 'secondaryColor',
  footerTextColor: 'textTertiaryColor',
  headlineColor: 'textDefaultColor',
  sideMenuTextColor: 'textDefaultColor',
  formBorderRadius: 'borderRadius',
  cardMobileBorderRadius: 'cardBorderRadius',
  cardRoomTypeColor: 'cardBodyColor',
  textRoomTypeColor: 'textDefaultColor',
}

const CURRENT_THEME_KEY = 'CURRENT_THEME'
const SHOW_THEME_CONFIG_FLAG = 'SHOW_THEME_CONFIG'
export interface InitThemeParams{
  themes: AppTheme[],
  defaultTheme: string,
}
@Injectable({
  providedIn: 'root'
})
export class ThemeService {

  private _currentAppTheme: AppTheme
  private _themes: AppTheme[] = []
  private _defaultTheme: string;
  constructor(
    private http: HttpClient,
    private xnStorage: XnStorageService,
    @Inject(APP_CONFIG) protected config: IAppConfig,
    private log: LogService,
  ) { 
    (window as any).appTheme = this;
  }

  initTheme(params: InitThemeParams) {
    this.log.log(`Init theme service defaultTheme=${params.defaultTheme}`);
    this._themes = params.themes;
    this._defaultTheme = params.defaultTheme;

    const themeLocal = this.getThemeFromLocal();
    if(themeLocal && themeLocal.name !== params.defaultTheme) {
      this.log.log('Load theme from local storage');
      this.setCurrentTheme(themeLocal, false);
    } else {
      this.log.log('Load default theme');
      this.setCurrentThemeByName(this._defaultTheme, false);
    }

    if(this.config.enableConfigTheme) {
      this.initShowThemeStorage();
    }
  }

  private $changeCurrentThemeSubject = new Subject<AppTheme>();
  setCurrentTheme(theme: AppTheme, saveToLocal = false) {
    var body = document.querySelector('body');
    if (this._currentAppTheme) {
      body.classList.remove(this._currentAppTheme.name.toLowerCase());
    }

    this._currentAppTheme = theme;
    body.classList.add(this._currentAppTheme.name.toLowerCase());
    if(saveToLocal){
      this.saveThemeToLocal(this._currentAppTheme);
    }
    
    this.$changeCurrentThemeSubject.next(this._currentAppTheme)
    this.applyThemeForAppWhenRun();
  }

  onChangeCurrentTheme():  Observable<AppTheme>{
    return this.$changeCurrentThemeSubject.asObservable();
  }

  setCurrentThemeByName(themeName: string, saveToLocal = false) {
    if(!this._themes.length) {
      return;
    }

    let themeData: AppTheme;
    themeData = this._themes.find(e => e.name === themeName);

    if(themeData) {
      this.setCurrentTheme(themeData, saveToLocal);
    }

  }

  applyThemeForAppWhenRun() {
    if (!this._currentAppTheme || !this._currentAppTheme.schema) {
      return;
    }

    const schema = this._currentAppTheme.schema;
    var r = document.querySelector(':root');

    this.setFaviconIcon();

    //Set variable by schema key
    for (let key in schema) {
      (r as any).style.setProperty(MapAppThemeVariable[key], this.getSchemaValue(key));
    }
  }

  getSchemaValue(key: string) {
    let result = '';
    const schema = this._currentAppTheme.schema; 
    if(!schema[key]) {
      if(ReplaceKeySchema[key] && schema[ReplaceKeySchema[key]]) {
        result = schema[ReplaceKeySchema[key]]
      } else {
        result = DefaultAppTheme.schema[key];
      };
    } else {
      result = schema[key];
    };

    if(key == 'fontPrimary' || key == 'fontSecondary') {
      if(result) {
        const fontOption = FONT_FAMILY_OPTION.find(f => f.code == result);
        if(fontOption && fontOption.linkImport) {
          const link = document.createElement('link'),
                head = document.querySelector('head');
          link.href = fontOption.linkImport;
          link.rel = 'stylesheet';
          head.appendChild(link);
        }
      }
      result = `'${result}', sans-serif`;
    }


    return result;
  }

  getThemes(): AppTheme[] {
    return this._themes || [];
  }

  getCurrentTheme(): AppTheme {
    return this._currentAppTheme;
  }

  loadRemoteTheme(url: string, done?: Function) {
    if(!url) {
      done && done();
      return;
    } 
      
    this.http.get<ReadRemoteThemeRS>(url).subscribe((res: ReadRemoteThemeRS) => {
      if(!res) return;

      if(res.themes) {
        this.themeAdd(res.themes);
      };

      if(res.defaultTheme) {
        this.setCurrentThemeByName(res.defaultTheme)
      };
      
      done && done();
    });
  }

  themeAdd(themes: AppTheme[]) {
    this._themes = this._themes.concat(themes);
  }


  saveThemeToLocal(theme: AppTheme) {
    this.xnStorage.setItem(CURRENT_THEME_KEY, JSON.stringify(theme));
  }

  getThemeFromLocal(): AppTheme | null {
    const themeStr = this.xnStorage.getItem(CURRENT_THEME_KEY);
    if (themeStr) {
      let result = JSON.parse(themeStr) as AppTheme;
      return result ? result : null
    } 

    return null;
  }

  exportThemeToJsonFile(theme: AppTheme): void {
    const themeJson = JSON.stringify(theme, null, 2); // Convert theme object to JSON string
    const blob = new Blob([themeJson], { type: 'application/json' }); // Create a blob with the JSON data
    const url = window.URL.createObjectURL(blob); // Create a URL for the blob

    // Create a temporary anchor element to trigger the download
    const link = document.createElement('a');
    link.href = url;
    link.download = `${theme.name}.json`;
    link.click();

    // Clean up
    window.URL.revokeObjectURL(url);
  }

  initShowThemeStorage() {
    if(this.xnStorage.getItem(SHOW_THEME_CONFIG_FLAG) == 0) {
      return;
    } else {
      this.setOffShowThemeStorage();
    }
  }

  setOnShowThemeStorage() {
    this.xnStorage.setItem(SHOW_THEME_CONFIG_FLAG, 1);
  }

  setOffShowThemeStorage() {
    this.xnStorage.setItem(SHOW_THEME_CONFIG_FLAG, 0);
  }

  getShowThemeStorage() {
    return this.xnStorage.getItem(SHOW_THEME_CONFIG_FLAG);
  }

  checkShowConfigTheme(params?: any): boolean {
    if(!this.config.enableConfigTheme) {
      return false;
    }

    if(!params) {
      return this.config.enableConfigTheme;
    }

    if(params && params.showThemeConfig == 1) {
      this.setOnShowThemeStorage()
      return true;
    } else {
      return this.getShowThemeStorage() == 1 ? true : false;
    }
  }

  setFaviconIcon() {
    let faviconUrl = 'assets/default_favicon.ico';
    if(!this._currentAppTheme) return;

    if (this._currentAppTheme.schema.faviconUrl) {
      faviconUrl = this._currentAppTheme.schema.faviconUrl;
    } 

    const link = document.createElement('link'),
      head = document.querySelector('head');
    link.href = faviconUrl;
    link.rel = 'icon';
    link.type = 'image/x-icon'
    head.appendChild(link);
  }

  getSiteName() {
    return this._currentAppTheme.schema.siteName || DefaultAppTheme.schema.siteName;
  }

  getShortSiteName() {
    return this._currentAppTheme.schema.shortSiteName || this._currentAppTheme.schema.siteName || DefaultAppTheme.schema.siteName;
  }

  isSeparateStudentForm() {
    return this._currentAppTheme.schema.separateProfileContactDetailFormToTwoSections == true ? true : false;
  }

  isUseExpandedFooter(): boolean {
    if(!this._currentAppTheme || !this._currentAppTheme.schema){
      return false;
    }

    return this._currentAppTheme.schema.useExpandedFooter == true;
  }

  isUseSearchIconFont(): boolean {
    if(!this._currentAppTheme || !this._currentAppTheme.schema){
      return false;
    }

    return this._currentAppTheme.schema.useSearchIconFont == true;
  }

  isCustomHeaderBackground(): boolean{
    if(!this._currentAppTheme || !this._currentAppTheme.schema){
      return false;
    }

    if(this._currentAppTheme.schema.headerBackgroundColor){
      return true;
    }
    return false;
  }
} 
 